import { useRef, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { isAlive } from 'mobx-state-tree'
import PropTypes from 'prop-types'
import ScrollToBottomButton from '../../components/ScrollToBottomButton'
import { ReactComponent as HeyNicIcon } from '../../assets/icons/HeyNicIconOnly.svg'
import { formatTime, groupMessagesByDay } from './utils'
import SuggestionPills from './SuggestionPills'
import ToolStatus from './ToolStatus'
import {
  inputOverhang,
  ScrollArea,
  Transcript,
  CenterColumn,
  EmptyState,
  EmptyStateMark,
  OpeningQuestion,
  DateSeparator,
  BotOuter,
  HumanRow,
  BotAvatar,
  MessageColumn,
  Timestamp,
  BotBubble,
  StreamingBubble,
  HumanBubble,
} from './styled'

const OPENING_QUESTION = 'Hi, I’m Nic. What can I help you with today?'

// Dead space at the bottom of the transcript, for the scroll-to-bottom button's
// "is there anything below the fold?" measurement: the strip the transcript now
// overhangs the input pill by (see Transcript), plus a little slack so the button
// doesn't sit right on the last line at rest.
const footerInset = compact => inputOverhang(compact) + 24

// How close to the bottom still counts as "parked at the bottom" — i.e. the
// transcript keeps following a streaming response.
const STICK_THRESHOLD = 40

// Minimum vertical finger travel that reads as "the user is scrolling the
// transcript" rather than a stray touch.
const DISMISS_DRAG = 12

// Shared chrome for a bot turn: icon, timestamp, bubble, follow-up pills.
const BotRow = ({ compact, timestamp, children, suggestions, onSuggestion, pillsDisabled }) => (
  <BotOuter compact={compact}>
    <BotAvatar className='bot-avatar'>
      <HeyNicIcon />
    </BotAvatar>
    <Timestamp className='bot-time'>{formatTime(timestamp)}</Timestamp>
    <MessageColumn className='bot-bubble'>{children}</MessageColumn>
    {/* Follow-up suggestions span the full column, right-aligned over Send. */}
    {suggestions?.length > 0 && (
      <div className='bot-suggest'>
        <SuggestionPills
          suggestions={suggestions}
          onSelect={onSuggestion}
          disabled={pillsDisabled}
          stacked
        />
      </div>
    )}
  </BotOuter>
)

export const BotMessage = observer(({ message, onSuggestion, compact, pillsDisabled, latest }) => {
  // The store swaps out activeChat after streaming (reconcile reload), which
  // kills these nodes; bail before reading a dead node until the parent
  // re-renders with the fresh tree.
  if (!isAlive(message)) return null

  const bubble = (
    <BotBubble loading={message.loading}>
      {message.text && <div style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>}
      {message.loading && (
        <div style={{ marginTop: message.text ? 8 : 0 }}>
          <ToolStatus
            currentStatus={message.currentStatusUpdate}
            statusUpdates={message.statusUpdates}
          />
        </div>
      )}
    </BotBubble>
  )

  return (
    <BotRow
      compact={compact}
      timestamp={message.timestamp}
      /* Only the newest turn offers follow-ups: the pills are what the agent
         wants asked NEXT, so leaving them on every past turn lets the user
         scroll up and re-ask a question the conversation has moved on from. */
      suggestions={latest && !message.loading ? message.suggestions : null}
      onSuggestion={onSuggestion}
      pillsDisabled={pillsDisabled}>
      {/* Animated blue border while the response streams in. */}
      {message.loading ? <StreamingBubble>{bubble}</StreamingBubble> : bubble}
    </BotRow>
  )
})

export const HumanMessage = observer(({ message }) => {
  if (!isAlive(message)) return null

  return (
    <HumanRow>
      <MessageColumn>
        <Timestamp align='right'>{formatTime(message.timestamp)}</Timestamp>
        <HumanBubble>{message.text}</HumanBubble>
      </MessageColumn>
    </HumanRow>
  )
})

const ChatView = ({
  store,
  chat: chatProp,
  onSuggestion,
  compact,
  flat,
  leadingTurns = [],
  hideEmptyState,
  pillsDisabled,
  onScrolledChange,
}) => {
  /*
   * `activeChat` is one slot for the whole app, shared with the SBSA panels. A
   * caller that owns a specific chat passes it (possibly null) so it can't render
   * a transcript that isn't its own — checked against `undefined` precisely so an
   * explicit null means "no chat", not "fall back to the global slot".
   */
  const chat = chatProp === undefined ? store.activeChat : chatProp
  const transcriptRef = useRef(null)
  const contentRef = useRef(null)
  const prevChatIdRef = useRef(null)
  // null until the first effect run, which seeds it from what is already on
  // screen rather than treating that as growth (see the effect below).
  const prevLenRef = useRef(null)
  // Whether the transcript should keep following a streaming response. Starts
  // true and only turns off when the user scrolls up to read something older.
  const stickToBottomRef = useRef(true)
  // Latest callback, so the touch/scroll listeners below don't re-subscribe (and
  // lose their drag state) every time the parent re-renders.
  const onScrolledChangeRef = useRef(onScrolledChange)
  onScrolledChangeRef.current = onScrolledChange

  const messages = chat?.displayMessages ?? []
  const messagesLength = messages.length
  const chatId = chat?.id ?? null
  /*
   * Everything on screen, client-side turns included — this is what "the
   * transcript grew" has to mean.
   *
   * The salary exchange (useSalaryPrompt) appends through `leadingTurns` and
   * usually runs with NO chat at all: Home's chat isn't created until the first
   * message actually goes to the agent, so `messagesLength` sits at 0 for the
   * whole conversation. Following only the persisted list would drop every reply
   * in below the fold on a compact layout and never chase it.
   */
  const turnCount = leadingTurns.length + messagesLength
  // With client-side turns there is always something to read, so the blank-chat
  // greeting never applies (see `leadingTurns`).
  const hasLeading = leadingTurns.length > 0
  const isBlank = (chat?.isBlank ?? false) && !hasLeading && !hideEmptyState
  /*
   * Taken from THIS chat's trailing message rather than the store's global
   * `responseLoading`: with the advice panels sharing that flag, a reply streaming
   * into some other chat would otherwise have this transcript chasing a stream
   * that will never appear in it. The two agree for the chat that owns the run.
   */
  const loading = chat?.lastMessage?.loading ?? false
  // Observed so the follow-the-stream effect below re-runs as tokens and status
  // steps arrive (the message object itself never changes identity).
  const streamedLength = chat?.lastMessage?.text?.length ?? 0
  const streamedStatus = chat?.lastMessage?.currentStatusUpdate?.update ?? ''

  useEffect(() => {
    const container = transcriptRef.current
    if (!container) return

    // Opening a different chat: jump to the newest message, no animation. A blank
    // chat has no newest message — jumping to the bottom of the empty state on a
    // keyboard-shortened viewport hides the greeting above the fold and leaves the
    // user to scroll up for it.
    if (chatId !== prevChatIdRef.current) {
      container.scrollTop = isBlank ? 0 : container.scrollHeight
      prevChatIdRef.current = chatId
      prevLenRef.current = turnCount
      stickToBottomRef.current = true
      return
    }

    /*
     * First run with no chat change to key off (the Home panel mounts with
     * `chatId` already null, so the branch above never fires). Record the
     * baseline WITHOUT scrolling: whatever is on screen at mount — the opening
     * comment, or a handed-over exchange — is the initial view, not new content,
     * and jumping to the bottom of a long alert on a short viewport hides the
     * start of the very thing the user came to read.
     */
    if (prevLenRef.current === null) {
      prevLenRef.current = turnCount
      return
    }

    // New turn: scroll to the bottom so the just-sent message and the reply are
    // in view. Sending — or tapping a pill — is an explicit "show me the newest"
    // action, so it re-arms following even if the user had scrolled up.
    if (turnCount > prevLenRef.current) {
      stickToBottomRef.current = true
      requestAnimationFrame(() => {
        const c = transcriptRef.current
        if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'auto' })
      })
    }
    prevLenRef.current = turnCount
  }, [chatId, turnCount, isBlank])

  /*
   * Follow the response as it streams in, so the status steps ("Looking at your
   * accounts…") and the text land in view rather than growing below the fold —
   * on mobile with the keyboard open there are only a couple hundred px of
   * transcript, so anything not followed happens off-screen. Gated on
   * stickToBottomRef: once the user scrolls up to read, we stop chasing and the
   * scroll-to-bottom button takes over.
   */
  useEffect(() => {
    if (!loading || !stickToBottomRef.current) return
    const container = transcriptRef.current
    if (container) container.scrollTop = container.scrollHeight
  }, [loading, streamedLength, streamedStatus])

  // Transcript-level touch/scroll wiring. Keyed on the chat and blank state
  // because those swap the transcript element itself.
  useEffect(() => {
    const container = transcriptRef.current
    if (!container) return undefined

    // Scrolling away from the bottom stops the stream-following above; coming
    // back within STICK_THRESHOLD resumes it. `onScrolledChange` reports whether
    // the transcript has scrolled at all, which is what the SBSA panel's fixed
    // header keys its drop shadow off.
    const handleScroll = () => {
      const distance = container.scrollHeight - container.scrollTop - container.clientHeight
      stickToBottomRef.current = distance <= STICK_THRESHOLD
      onScrolledChangeRef.current?.(container.scrollTop > 0)
    }

    /*
     * Dragging through the transcript dismisses the keyboard (mirrors 3Nickels
     * mobile): the keyboard covers most of the chat, so a scroll is a clear
     * signal the user wants to read rather than type. Driven by touch rather
     * than the scroll event on purpose — programmatic auto-scrolls fire `scroll`
     * too, and following a stream must not close the keyboard on its own.
     */
    let drag = null

    const handleTouchStart = e => {
      const touch = e.touches[0]
      drag = touch ? { x: touch.clientX, y: touch.clientY, dismissed: false } : null
    }

    const handleTouchMove = e => {
      if (!drag || drag.dismissed) return
      const touch = e.touches[0]
      if (!touch) return

      // Nothing to scroll (a brand-new chat): the drag isn't a "let me read"
      // signal, and dismissing anyway resizes the shell mid-gesture, which reads
      // as the input field jumping.
      if (container.scrollHeight <= container.clientHeight + STICK_THRESHOLD) return

      const dy = touch.clientY - drag.y
      const dx = touch.clientX - drag.x
      // Vertical intent only: a horizontal swipe is the empty state's scrolling
      // pill row, which shouldn't disturb the keyboard.
      if (Math.abs(dy) < DISMISS_DRAG || Math.abs(dy) <= Math.abs(dx)) return

      drag.dismissed = true
      const active = document.activeElement
      if (active?.tagName === 'TEXTAREA') active.blur()
    }

    handleScroll()
    container.addEventListener('scroll', handleScroll, { passive: true })
    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
    }
  }, [chatId, isBlank])

  // No chat yet and nothing client-side to show. The SBSA panel is the case where
  // there is no chat but there IS something to render (`leadingTurns`): a step's chat
  // isn't created until the user taps a pill.
  if (!chat && !hasLeading) return null

  // Brand-new chat: opening question + suggestion pills, set down from the top
  // (see EmptyState — mobile stays close to the top so the keyboard can't push
  // the greeting and pills out of the visible area).
  if (isBlank) {
    return (
      <ScrollArea>
        <Transcript ref={transcriptRef} compact={compact}>
          <CenterColumn compact={compact}>
            <EmptyState>
              {/* Hidden on mobile — the top nav's wordmark already covers it. */}
              <EmptyStateMark className='hn-empty-mark'>
                <HeyNicIcon width={45} height={45} />
              </EmptyStateMark>
              <OpeningQuestion>{OPENING_QUESTION}</OpeningQuestion>
              <SuggestionPills onSelect={onSuggestion} disabled={pillsDisabled} />
            </EmptyState>
          </CenterColumn>
        </Transcript>
      </ScrollArea>
    )
  }

  // The trailing turn of the transcript — the only one whose pills are still
  // live (see BotMessage). Identity comparison against the node, so it holds
  // whichever day group it lands in.
  const lastDisplayed = messages[messagesLength - 1]

  const renderMessage = (message, key) =>
    message.isHuman ? (
      <HumanMessage key={key} message={message} />
    ) : (
      <BotMessage
        key={key}
        message={message}
        onSuggestion={onSuggestion}
        compact={compact}
        pillsDisabled={pillsDisabled}
        latest={message === lastDisplayed}
      />
    )

  // `flat` drops the day separators. The SBSA panel is a short, single-sitting
  // conversation shown in a narrow card, and its design has none.
  const groups = flat ? [] : groupMessagesByDay(messages)
  let flatIndex = -1
  const nextKey = message => {
    flatIndex += 1
    return message.id ?? `m-${flatIndex}`
  }

  return (
    <ScrollArea>
      <Transcript ref={transcriptRef} compact={compact}>
        <CenterColumn compact={compact}>
          <div ref={contentRef}>
            {/* FE-authored turns: client-only, so they are not part of the
                persisted transcript and survive the post-stream reconcile. The
                opening comment is always the first; anything after it is a
                client-side exchange that never reached the agent (see
                useSalaryPrompt). */}
            {leadingTurns.map((turn, index) => {
              const key = `leading-${index}`
              if (turn.role === 'user') {
                return (
                  <HumanRow key={key}>
                    <MessageColumn>
                      <Timestamp align='right'>{formatTime(turn.timestamp)}</Timestamp>
                      <HumanBubble>{turn.text}</HumanBubble>
                    </MessageColumn>
                  </HumanRow>
                )
              }
              return (
                <BotRow
                  key={key}
                  compact={compact}
                  timestamp={turn.timestamp}
                  /* Only the newest client-side turn offers pills, and only
                     while it is still the newest thing on screen — an older
                     turn's pills re-ask a question the exchange has moved past,
                     and a real transcript underneath supersedes all of them. */
                  suggestions={
                    index === leadingTurns.length - 1 && messagesLength === 0
                      ? turn.suggestions
                      : null
                  }
                  onSuggestion={onSuggestion}
                  pillsDisabled={pillsDisabled}>
                  <BotBubble>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{turn.text}</div>
                  </BotBubble>
                </BotRow>
              )
            })}
            {flat
              ? messages.map(message => renderMessage(message, nextKey(message)))
              : groups.map(group => (
                  <div key={`${group.label}-${group.messages[0]?.timestamp}`}>
                    <DateSeparator>{group.label}</DateSeparator>
                    {group.messages.map(message => renderMessage(message, nextKey(message)))}
                  </div>
                ))}
          </div>
        </CenterColumn>
      </Transcript>
      <ScrollToBottomButton
        absolute
        bottomInset={footerInset(compact)}
        getScrollElement={() => transcriptRef.current}
        getBottomAnchor={() => contentRef.current}
        watch={`${turnCount}:${streamedLength}`}
      />
    </ScrollArea>
  )
}

ChatView.propTypes = {
  /** The chat to render. Defaults to the store's shared `activeChat`. */
  chat: PropTypes.object,
  onSuggestion: PropTypes.func,
  /** Narrow-container layout (icon + timestamp above a full-width bubble). */
  compact: PropTypes.bool,
  /** Render one flat list instead of day-grouped sections. */
  flat: PropTypes.bool,
  /** Client-only turns rendered above the persisted transcript, oldest first. */
  leadingTurns: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(['bot', 'user']),
      text: PropTypes.string.isRequired,
      timestamp: PropTypes.string,
      suggestions: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  /** Suppress the brand-new-chat greeting (the caller supplies its own opener). */
  hideEmptyState: PropTypes.bool,
  /** Dim + block the suggestion pills (e.g. while a response streams). */
  pillsDisabled: PropTypes.bool,
  /** Called with whether the transcript is scrolled away from the top. */
  onScrolledChange: PropTypes.func,
}

export default inject('store')(observer(ChatView))
